/**
 * Servicio centralizado para manejo de datos de la aplicación
 * Combina carga de datos de usuario y conferencia de forma inteligente
 */

import { Storage } from '@capacitor/storage';
import {
  USER_STORAGE_KEYS,
  EVENT_STORAGE_KEYS,
  CONFERENCE_STORAGE_KEYS
} from '../constants/storage';
import { Schedule, Session } from '../models/Schedule';
import { Speaker } from '../models/Speaker';
import { Location } from '../models/Location';

export interface AppDataState {
  // Estado de usuario
  isLoggedIn: boolean;
  hasSeenTutorial: boolean;
  username?: string;
  userced?: string;
  userid?: string;
  userother?: string;
  
  // Estado de evento
  eventcode?: string;
  eventName?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  eventLocation?: string;
  
  // Estado de datos de conferencia
  hasConferenceData: boolean;
  schedule?: Schedule;
  sessions?: Session[];
  speakers?: Speaker[];
  locations?: Location[];
  allTracks?: string[];
  
  // Metadatos
  lastUpdate?: string;
  dataLoaded: boolean;
}

export class AppDataService {
  private static instance: AppDataService;
  private currentState: AppDataState | null = null;

  static getInstance(): AppDataService {
    if (!AppDataService.instance) {
      AppDataService.instance = new AppDataService();
    }
    return AppDataService.instance;
  }

  /**
   * Carga el estado completo de la aplicación
   */
  async loadAppState(): Promise<AppDataState> {
    console.log('📊 AppDataService: Cargando estado completo de la aplicación...');
    
    try {
      // Cargar datos de usuario
      const userData = await this.loadUserData();
      
      // Cargar datos de conferencia si el usuario está logueado
      let conferenceData: Partial<AppDataState> = {
        hasConferenceData: false,
        schedule: undefined,
        sessions: [],
        speakers: [],
        locations: [],
        allTracks: [],
        lastUpdate: undefined,
        dataLoaded: false
      };

      if (userData.isLoggedIn && userData.eventcode && userData.eventcode !== 'nada') {
        console.log('👤 Usuario logueado detectado, cargando datos de conferencia...');
        const loadedConferenceData = await this.loadConferenceData(userData.eventcode);
        conferenceData = { ...conferenceData, ...loadedConferenceData };
      } else {
        console.log('👤 Usuario no logueado o sin evento, saltando datos de conferencia');
      }
      
      // Combinar estados con valores por defecto
      this.currentState = {
        // Valores por defecto
        isLoggedIn: false,
        hasSeenTutorial: false,
        hasConferenceData: false,
        sessions: [],
        speakers: [],
        locations: [],
        allTracks: [],
        dataLoaded: false,
        // Sobrescribir con datos reales
        ...userData,
        ...conferenceData
      } as AppDataState;

      console.log('✅ AppDataService: Estado cargado exitosamente', {
        isLoggedIn: this.currentState.isLoggedIn,
        hasSeenTutorial: this.currentState.hasSeenTutorial,
        eventcode: this.currentState.eventcode,
        hasConferenceData: this.currentState.hasConferenceData,
        sessionsCount: this.currentState.sessions?.length || 0,
        speakersCount: this.currentState.speakers?.length || 0
      });

      return this.currentState;
      
    } catch (error) {
      console.error('❌ AppDataService: Error al cargar estado:', error);
      
      // Retornar estado por defecto en caso de error
      this.currentState = this.getDefaultState();
      return this.currentState;
    }
  }

  /**
   * Carga solo los datos de usuario
   */
  private async loadUserData(): Promise<Partial<AppDataState>> {
    console.log('👤 Cargando datos de usuario...');
    
    try {
      const [
        hasLoggedIn,
        hasSeenTutorial,
        username,
        userced,
        userid,
        userother,
        eventcode,
        eventName,
        eventStartDate,
        eventEndDate,
        eventLocation
      ] = await Promise.all([
        Storage.get({ key: USER_STORAGE_KEYS.HAS_LOGGED_IN }),
        Storage.get({ key: USER_STORAGE_KEYS.HAS_SEEN_TUTORIAL }),
        Storage.get({ key: USER_STORAGE_KEYS.USERNAME }),
        Storage.get({ key: USER_STORAGE_KEYS.USER_CED }),
        Storage.get({ key: USER_STORAGE_KEYS.USER_ID }),
        Storage.get({ key: USER_STORAGE_KEYS.USER_OTHER }),
        Storage.get({ key: EVENT_STORAGE_KEYS.EVENT_CODE }),
        Storage.get({ key: EVENT_STORAGE_KEYS.EVENT_NAME }),
        Storage.get({ key: EVENT_STORAGE_KEYS.EVENT_START_DATE }),
        Storage.get({ key: EVENT_STORAGE_KEYS.EVENT_END_DATE }),
        Storage.get({ key: EVENT_STORAGE_KEYS.EVENT_LOCATION })
      ]);

      return {
        isLoggedIn: hasLoggedIn.value === 'true',
        hasSeenTutorial: hasSeenTutorial.value === 'true',
        username: username.value || undefined,
        userced: userced.value || undefined,
        userid: userid.value || undefined,
        userother: userother.value || undefined,
        eventcode: eventcode.value || undefined,
        eventName: eventName.value || undefined,
        eventStartDate: eventStartDate.value || undefined,
        eventEndDate: eventEndDate.value || undefined,
        eventLocation: eventLocation.value || undefined
      };
      
    } catch (error) {
      console.error('❌ Error al cargar datos de usuario:', error);
      return {
        isLoggedIn: false,
        hasSeenTutorial: false
      };
    }
  }

  /**
   * Carga datos de conferencia para un evento específico
   */
  private async loadConferenceData(eventCode: string): Promise<Partial<AppDataState>> {
    console.log(`📅 Cargando datos de conferencia para evento: ${eventCode}`);
    
    try {
      // Verificar si hay datos cargados
      const [
        dataLoaded,
        lastEventCode,
        scheduleData,
        speakersData,
        locationsData,
        lastUpdate
      ] = await Promise.all([
        Storage.get({ key: CONFERENCE_STORAGE_KEYS.DATA_LOADED }),
        Storage.get({ key: EVENT_STORAGE_KEYS.EVENT_CODE }),
        Storage.get({ key: CONFERENCE_STORAGE_KEYS.SCHEDULE_DATA }),
        Storage.get({ key: CONFERENCE_STORAGE_KEYS.SPEAKERS_DATA }),
        Storage.get({ key: CONFERENCE_STORAGE_KEYS.LOCATIONS_DATA }),
        Storage.get({ key: CONFERENCE_STORAGE_KEYS.LAST_UPDATE })
      ]);

      // Verificar si los datos son del evento correcto
      if (dataLoaded.value !== 'true' || lastEventCode.value !== eventCode) {
        console.log('📅 No hay datos locales válidos, intentando cargar desde API...');
        return await this.loadConferenceDataFromAPI(eventCode);
      }

      // Parsear datos almacenados
      let schedule: Schedule | undefined;
      let sessions: Session[] = [];
      let speakers: Speaker[] = [];
      let locations: Location[] = [];
      let allTracks: string[] = [];

      if (scheduleData.value) {
        try {
          const parsed = JSON.parse(scheduleData.value);
          schedule = parsed.schedule;
          sessions = parsed.sessions || [];
          allTracks = parsed.allTracks || [];
        } catch (e) {
          console.warn('⚠️ Error al parsear datos de agenda:', e);
        }
      }

      if (speakersData.value) {
        try {
          speakers = JSON.parse(speakersData.value);
        } catch (e) {
          console.warn('⚠️ Error al parsear datos de speakers:', e);
        }
      }

      if (locationsData.value) {
        try {
          locations = JSON.parse(locationsData.value);
        } catch (e) {
          console.warn('⚠️ Error al parsear datos de ubicaciones:', e);
        }
      }

      const hasConferenceData = !!(schedule && sessions.length > 0);

      console.log('✅ Datos de conferencia cargados desde storage local', {
        hasSchedule: !!schedule,
        sessionsCount: sessions.length,
        speakersCount: speakers.length,
        locationsCount: locations.length
      });

      return {
        hasConferenceData,
        schedule,
        sessions,
        speakers,
        locations,
        allTracks,
        lastUpdate: lastUpdate.value || undefined,
        dataLoaded: true
      };

    } catch (error) {
      console.error('❌ Error al cargar datos de conferencia:', error);
      return {
        hasConferenceData: false,
        sessions: [],
        speakers: [],
        locations: [],
        allTracks: [],
        dataLoaded: false
      };
    }
  }

  /**
   * Carga datos de conferencia desde la API
   */
  private async loadConferenceDataFromAPI(eventCode: string): Promise<Partial<AppDataState>> {
    console.log(`🌐 Cargando datos desde API para evento: ${eventCode}`);
    
    try {
      // Usar DataLoadingService existente
      const { default: DataLoadingService } = await import('./DataLoadingService');
      const dataLoadingService = DataLoadingService.getInstance();
      
      const result = await dataLoadingService.loadEventData(eventCode, true);
      
      if (result.success) {
        console.log('✅ Datos cargados desde API exitosamente');
        return {
          hasConferenceData: result.hasSchedule,
          schedule: result.schedule,
          sessions: result.sessions || [],
          speakers: result.speakers || [],
          locations: result.locations || [],
          allTracks: result.allTracks || [],
          lastUpdate: new Date().toISOString(),
          dataLoaded: true
        };
      } else {
        console.log('❌ Error al cargar datos desde API');
        return {
          hasConferenceData: false,
          sessions: [],
          speakers: [],
          locations: [],
          allTracks: [],
          dataLoaded: false
        };
      }
      
    } catch (error) {
      console.error('❌ Error al cargar desde API:', error);
      return {
        hasConferenceData: false,
        sessions: [],
        speakers: [],
        locations: [],
        allTracks: [],
        dataLoaded: false
      };
    }
  }

  /**
   * Obtiene el estado actual (cached)
   */
  getCurrentState(): AppDataState | null {
    return this.currentState;
  }

  /**
   * Fuerza la recarga de datos
   */
  async reloadData(): Promise<AppDataState> {
    this.currentState = null;
    return await this.loadAppState();
  }

  /**
   * Estado por defecto
   */
  private getDefaultState(): AppDataState {
    return {
      // Estado de usuario
      isLoggedIn: false,
      hasSeenTutorial: false,
      username: undefined,
      userced: undefined,
      userid: undefined,
      userother: undefined,

      // Estado de evento
      eventcode: undefined,
      eventName: undefined,
      eventStartDate: undefined,
      eventEndDate: undefined,
      eventLocation: undefined,

      // Estado de datos de conferencia
      hasConferenceData: false,
      schedule: undefined,
      sessions: [],
      speakers: [],
      locations: [],
      allTracks: [],

      // Metadatos
      lastUpdate: undefined,
      dataLoaded: false
    };
  }
}

// Exportar instancia singleton
export const appDataService = AppDataService.getInstance();
